// My Leaves — WP plugin parity (Screen E3).
// 7-col table: Type / From / To / Reason / Status / Admin Note / Action.
// Cancel button on pending rows. + Apply for Leave btn opens ApplyLeaveModal.

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, EmptyState, Skeleton, REQUEST_TONES } from "../../Components/ui";
import ApplyLeaveModal from "./ApplyLeaveModal";

const TYPE_LABELS = {
  annual: "Annual Leave", sick: "Sick Leave", casual: "Casual Leave",
  marriage: "Marriage Leave", bereavement: "Bereavement Leave",
  examination: "Examination Leave", paternity: "Paternity Leave",
  maternity: "Maternity Leave", half_day: "Half Day", unpaid: "Unpaid Leave",
};
// Shared approval-lifecycle tones (see Components/ui/statusTones.js). Was a
// private copy of four hardcoded light-theme pairs, duplicated across five
// pages and unreadable-looking in dark mode.
const STATUS_STYLE = REQUEST_TONES;

export default function MyLeavesPage() {
  const toast = useToast();
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);

  useShellHeader({ title: "My Leaves" }, []);

  async function load() {
    setLoading(true);
    try {
      const year = new Date().getUTCFullYear();
      const [r, b] = await Promise.all([
        api("/leaves/my"),
        api(`/leaves/balance/me?year=${year}`).catch(() => null),
      ]);
      setLeaves(r.leaves || []);
      setBalance(b?.balance || null);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function cancel(l) {
    const msg = l.status === "approved"
      ? `Cancel your approved ${TYPE_LABELS[l.leaveType] || l.leaveType} for ${fmtDate(l.fromDate)}${fmtDate(l.fromDate) === fmtDate(l.toDate) ? "" : " → " + fmtDate(l.toDate)}? Your balance will be restored and admins will be notified.`
      : "Cancel this leave request?";
    if (!window.confirm(msg)) return;
    try {
      await api(`/leaves/${l._id}`, { method: "DELETE" });
      toast({ title: "Leave cancelled.", status: "success" });
      load();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  // Owner can cancel pending leaves, or APPROVED leaves whose fromDate is strictly in the future.
  function canCancel(l) {
    if (l.status === "pending") return true;
    if (l.status !== "approved") return false;
    const now = new Date();
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return new Date(l.fromDate) > todayStart;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>My Leaves</h2>
        <button
          onClick={() => setApplyOpen(true)}
          style={{
            background: "var(--accent-primary)", color: "white", border: "none",
            padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >+ Apply for Leave</button>
      </div>

      {balance && <BalanceCard balance={balance} />}

      <Card padding="none">
        {loading ? (
          <div style={{ padding: 16 }}><Skeleton.Row count={4} /></div>
        ) : leaves.length === 0 ? (
          <EmptyState icon="📅" title="No leave requests yet" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>Type</Th><Th>From</Th><Th>To</Th><Th>Days</Th><Th>Reason</Th>
                  <Th>Status</Th><Th>Admin Note</Th><Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {leaves.map((l) => {
                  const s = STATUS_STYLE[l.status] || STATUS_STYLE.pending;
                  return (
                    <tr key={l._id}>
                      <Td>{TYPE_LABELS[l.leaveType] || l.leaveType}</Td>
                      <Td className="tabular">{fmtDate(l.fromDate)}</Td>
                      <Td className="tabular">{fmtDate(l.toDate)}</Td>
                      <Td className="tabular" style={{ fontWeight: 600 }}>{leaveDays(l)}</Td>
                      <Td style={{ maxWidth: 400, color: "var(--text-secondary)" }}>{l.reason || "—"}</Td>
                      <Td>
                        <span style={{
                          background: s.bg, color: s.co, padding: "2px 10px",
                          borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                        }}>{l.status}</span>
                      </Td>
                      <Td style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{l.adminNote || "—"}</Td>
                      <Td>
                        {canCancel(l) ? (
                          <button onClick={() => cancel(l)} style={btnGhost}>Cancel</button>
                        ) : "—"}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ApplyLeaveModal open={applyOpen} onClose={() => setApplyOpen(false)} onSubmitted={() => { setApplyOpen(false); load(); }} />
    </div>
  );
}

function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }
// Inclusive calendar-day span (half-day leaves count as 0.5). Falls back to
// computing from from/to when the server-stored `days` is 0 (e.g. for leaves
// migrated from WP where the field was never populated).
function leaveDays(l) {
  if (l.leaveType === "half_day") return "0.5";
  if (typeof l.days === "number" && l.days > 0) return String(l.days);
  try {
    const a = new Date(l.fromDate), b = new Date(l.toDate);
    return String(Math.round((b - a) / 86400000) + 1);
  } catch { return "—"; }
}

const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-primary)" };
const btnGhost = {
  background: "transparent", color: "var(--tone-danger-fg)",
  padding: "4px 10px", border: "1px solid var(--border-default)", borderRadius: 6,
  fontSize: 12, cursor: "pointer", fontWeight: 500,
};
function Th({ children }) { return <th style={thStyle}>{children}</th>; }
function Td({ children, style, className }) { return <td style={{ ...tdBase, ...style }} className={className}>{children}</td>; }

// Compact "what I have left this year" card — first thing employee sees on
// /leaves. Pulls from /api/leaves/balance/me which already merges actual
// approved leaves with any admin override.
function BalanceCard({ balance }) {
  const policy = balance.policy || { annual: 7, sick: 10, casual: 8, wfhPerMonth: 3 };
  const annTotal = (policy.annual || 0) + (balance.carryOver || 0);
  const items = [
    { key: "annual", label: "Annual",  rem: balance.remaining?.annual ?? 0, used: balance.used?.annual ?? 0, total: annTotal },
    { key: "sick",   label: "Sick",    rem: balance.remaining?.sick   ?? 0, used: balance.used?.sick   ?? 0, total: policy.sick },
    { key: "casual", label: "Casual",  rem: balance.remaining?.casual ?? 0, used: balance.used?.casual ?? 0, total: policy.casual },
  ];
  const wfhCap   = policy.wfhPerMonth || policy.wfh || 3;
  const wfhMonth = balance.actual?.wfhThisMonth ?? 0;
  const wfhRem   = Math.max(0, wfhCap - wfhMonth);

  return (
    <Card padding="none">
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>My balance · {balance.yearRef}</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>
            Total leaves taken this year: <b>{balance.actual?.totalLeaveDays ?? 0}</b>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 0, borderTop: "1px solid var(--border-subtle)" }}>
        {items.map((it) => {
          const ratio = it.total > 0 ? it.rem / it.total : 0;
          const color = it.rem <= 0 ? "var(--tone-danger-fg)" : (ratio <= 0.25 ? "var(--tone-warning-fg)" : "var(--tone-success-fg)");
          return (
            <div key={it.key} style={{ padding: "16px 18px", borderRight: "1px solid var(--border-subtle)" }}>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{it.label}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color }}>{it.rem}</span>
                <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>of {it.total} left</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{it.used} used</div>
            </div>
          );
        })}
        <div style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>WFH this month</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: wfhRem <= 0 ? "var(--tone-danger-fg)" : "var(--tone-success-fg)" }}>{wfhRem}</span>
            <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>of {wfhCap} left</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{wfhMonth} used</div>
        </div>
      </div>
    </Card>
  );
}
